import React from "react";
import { Link } from "react-router-dom";
import { Globe, ShieldCheck } from "lucide-react";
import { BRAND, FOOTER_NAV } from "../../lib/site-data";

function FooterLink({ link }) {
 const label = typeof link === "string" ? link : link.label;
 const href = typeof link === "string" ? null : link.href;
 const external = typeof link === "object" && link.external;
 const className =
 "text-[14.5px] text-ink/80 hover:text-forest transition-colors";

 // Untyped strings (legacy) just render as plain text - no dead links.
 if (!href) return <span className={className}>{label}</span>;

 // Same-origin route (e.g. /for-doctors) → SPA navigation.
 // Hashes that target the home page (/#…) → SPA navigation with hash.
 if (href.startsWith("/") && !external) {
 return (
 <Link to={href} className={className}>
 {label}
 </Link>
 );
 }

 // External (https://… or wa.me) → new-tab.
 return (
 <a
 href={href}
 target={external ? "_blank" : undefined}
 rel={external ? "noopener noreferrer" : undefined}
 className={className}
 >
 {label}
 </a>
 );
}

export default function Footer() {
 return (
 <footer
 data-testid="site-footer"
 className="bg-cream pt-24 pb-12 border-t border-ink/10"
 >
 <div className="container-x">
 <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
 <div className="lg:col-span-4">
 <Link
 to="/"
 className="inline-flex items-center gap-2.5 text-ink"
 >
 <img
 src={BRAND.logoUrl}
 alt=""
 aria-hidden="true"
 className="h-9 w-9 rounded-2xl object-contain"
 />
 <span className="font-display text-[18px] font-extrabold tracking-tight">
 {BRAND.name}
 </span>
 </Link>
 <p className="mt-2 text-[12.5px] text-ink/55">
 A product of <span className="font-semibold text-ink/80">Hivebrain LTD</span>
 </p>
 <p className="mt-5 text-[15px] text-ink/65 max-w-sm leading-relaxed">
 WhatsApp-first telemedicine for Nigeria. Licensed clinicians, AI-powered care guidance, secure records.
 </p>
 <div className="mt-6 flex flex-wrap items-center gap-3 text-[12.5px] text-ink/60">
 <span className="inline-flex items-center gap-1.5 rounded-full border border-ink/10 px-3 py-1.5">
 <ShieldCheck className="h-3.5 w-3.5 text-forest" />
 HIPAA-aligned controls
 </span>
 <span className="inline-flex items-center gap-1.5 rounded-full border border-ink/10 px-3 py-1.5">
 <Globe className="h-3.5 w-3.5 text-forest" />
 EN · YOR · IGB · HAU
 </span>
 </div>
 </div>

 <div className="lg:col-span-8 grid grid-cols-2 sm:grid-cols-4 gap-10">
 {FOOTER_NAV.map((col) => (
 <div key={col.title}>
 <p className="text-[12px] uppercase tracking-[0.18em] text-ink/50 font-semibold">
 {col.title}
 </p>
 <ul className="mt-4 space-y-3">
 {col.links.map((l) => (
 <li key={typeof l === "string" ? l : l.label}>
 <FooterLink link={l} />
 </li>
 ))}
 </ul>
 </div>
 ))}
 </div>
 </div>

 <div className="mt-16 pt-8 border-t border-ink/10 text-[13px] text-ink/60">
 <p>
 © {new Date().getFullYear()} {BRAND.name} by Hivebrain LTD. All rights reserved. Care delivered by independently licensed clinicians.
 </p>
 <p className="mt-2 text-[12.5px] text-ink/50">
 telemedcare.app is owned and operated by Hivebrain LTD.
 </p>
 </div>
 <p className="mt-6 text-[11.5px] text-ink/40 max-w-3xl leading-relaxed">
 Testimonials reflect individual experiences. Results may vary. Photos shown may include models.
 </p>
 </div>
 </footer>
 );
}
